/**
 * CalculateListPresenter
 * --
 */
import React, { useState } from 'react';
import { Content, Title, Row, Col, Information, ModalLayout } from 'components';
import { Statistic, Button, Table, Calendar, Tag, Spin } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { PRODUCT_TYPE } from 'constants/ProductConstants';
import { COMMISSION_TYPE } from 'constants/CommissionConstants';
import { stringToMoneyFormat, isNull } from 'utils';
import { PURCHASE_STATUS } from 'constants/OrderConstants';
import Payment from 'constants/Payment';
import ShopInformationList from 'routes/admin.routes/admin.orders/OrderList/components/ShopInformationList';
import { downloadXlsx } from 'utils/XlsxDownloader';

/**----------------------------------------
 * [Component] 정산관리 > 정산내역 - 프레젠터
 * ----------------------------------------
 */
const CalculateListPresenter = ({
  permission,
  loading,
  calculateList,
  setCurrentShop,
  shopList,
  currentShop,
  searchKeyword,
  setSearchKeyword,
  currentDate,
  onChangeMonth,
  selectedList,
  setSelectedList,
  onCalculation,
  onGetCalculationPrice,
  isModalOpened,
  setIsModalOpened,
  authInfo,
}) => {
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  /* ===== STATE ===== */
  const [viewType, setViewType] = useState('list');
  const [clickDate, setClickDate] = useState(null);
  const [isDrawerOpened, setIsDrawerOpened] = useState(false);

  /* ===== VARIABLE ===== */
  // 합계금액;
  const totalPrice =
    calculateList.length > 0
      ? calculateList.reduce((acc, cur) => acc + cur.payment_price, 0)
      : 0;
  // 합계금액;
  const expectedTotalPrice = selectedList
    ? selectedList.reduce((acc, cur) => acc + cur.payment_price, 0)
    : 0;

  /* ===== FUNCTION ===== */
  const handleDayClick = ({ $d }) => {
    setClickDate($d);
    setIsDrawerOpened(!isDrawerOpened);
  };
  /* ===== HOOKS ===== */
  /* ===== RENDER ===== */
  // 전체 통계
  const allCalculation = onGetCalculationPrice(calculateList);
  const finalCalculation = onGetCalculationPrice(selectedList);

  // JSX: start
  return (
    <>
      <Content
        maxWidth={'100%'}
        height={'100%'}
        padding={20}
        backgroundColor={'none'}
      >
        <Title size={3} style={{ marginBottom: 10 }}>
          정산내역
        </Title>
        <Spin spinning={loading}>
          <Row gutter={[1, 1]}>
            {/* === 업체목록 === */}
            {permission === 'ADMIN' && (
              <Col x={4}>
                <Content
                  maxWidth={'100%'}
                  height={'100%'}
                  minHeight={540}
                  padding={0}
                  backgroundColor={'#fff'}
                  className="boxShadow"
                >
                  <ShopInformationList onChange={setCurrentShop} />
                </Content>
              </Col>
            )}
            {/* === 업체목록 === */}

            <Col x={permission === 'ADMIN' ? 20 : 24}>
              {/* ===== 하단 컨텐츠 [업체목록+정산목록] ===== */}

              <Content
                maxWidth={'100%'}
                height={'100%'}
                minHeight={540}
                padding={0}
                backgroundColor={'#fff'}
                className="boxShadow"
              >
                {/* === 정산목록 헤더 === */}
                <Content
                  maxWidth={'100%'}
                  style={{
                    height: 53,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <h2
                    style={{
                      fontSize: 18,
                      fontWeight: 600,
                      color: '#404040',
                      lineHeight: '133%',
                      width: '33.33%',
                    }}
                  >
                    정산내역
                  </h2>

                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 25,
                      fontWeight: 700,
                      width: '33.33%',
                    }}
                  >
                    <Button
                      shape="circle"
                      onClick={() => onChangeMonth('left')}
                      disabled={currentShop === null}
                    >
                      <LeftOutlined />
                    </Button>
                    <span
                      style={{
                        padding: '0px 10px',
                        width: 165,
                        textAlign: 'center',
                      }}
                    >
                      {currentDate.year}년 {currentDate.month}월
                    </span>
                    <Button
                      shape="circle"
                      onClick={() => onChangeMonth('right')}
                      disabled={currentShop === null}
                    >
                      <RightOutlined />
                    </Button>
                  </div>

                  <div style={{ width: '33.33%', textAlign: 'right' }}>
                    {permission === 'ADMIN' && (
                      <Button
                        style={{ marginRight: 4 }}
                        disabled={currentShop === null}
                      >
                        정산메일발송
                      </Button>
                    )}
                    <Button
                      style={{ marginRight: 4 }}
                      disabled={currentShop === null}
                      onClick={() =>
                        downloadXlsx({
                          columns: [
                            {
                              title: '주문번호',
                              dataIndex: 'shop_order_id',
                            },
                            {
                              title: '정산',
                              dataIndex: 'purchase_status',
                            },
                            {
                              title: '상품유형',
                              dataIndex: 'type',
                            },
                            {
                              title: '상품명',
                              dataIndex: 'product_name',
                            },
                            {
                              title: '결제일',
                              dataIndex: 'created_at',
                            },
                            {
                              title: '결제수단',
                              dataIndex: 'payment_method',
                            },
                            {
                              title: '결제금액',
                              dataIndex: 'payment_price',
                            },
                            {
                              title:
                                authInfo?.type === 'ADMIN'
                                  ? '매출이익'
                                  : '수수료',
                              key: 'commission_rate',
                              dataIndex: 'commission_rate',
                            },
                            {
                              title:
                                authInfo?.type === 'ADMIN'
                                  ? '정산예정금액(매출이익)'
                                  : '정산예정금액(수수료)',
                              key: 'calc_price',
                              dataIndex: 'calc_price',
                              width: 165,
                              align: 'right',
                              fixed: 'right',
                              render: (_, row) => {
                                const price = row?.payment_price ?? 0;
                                const rate =
                                  row?.product?.commission?.commission_rate ??
                                  0;
                                const cms = price * (rate / 100);
                                const total = price - cms;

                                return `${stringToMoneyFormat(
                                  total
                                )}(${stringToMoneyFormat(cms)})`;
                              },
                            },
                          ],
                          rows: calculateList?.map((_item) => {
                            const price = _item?.payment_price ?? 0;
                            const rate =
                              _item?.product?.commission?.commission_rate ?? 0;
                            const cms = price * (rate / 100);
                            const total = price - cms;
                            return {
                              ..._item,
                              product_name: _item?.product?.product_name,
                              commission_rate: `${stringToMoneyFormat(rate)} %`,
                              calc_price: `${stringToMoneyFormat(
                                total
                              )}(${stringToMoneyFormat(cms)}}`,
                            };
                          }),
                          fileName: '정산내역',
                        })
                      }
                    >
                      엑셀로 다운로드
                    </Button>
                    <Button
                      type="primary"
                      style={{ width: 165 }}
                      onClick={() => setIsModalOpened(!isModalOpened)}
                      disabled={currentShop === null}
                    >
                      {!isNull(selectedList)
                        ? `[${selectedList.length}]개 상품 `
                        : ''}
                      정산하기
                    </Button>
                  </div>
                </Content>
                {/* === 정산목록 헤더 === */}

                {/* === 정산목록 === */}
                <Content maxWidth={'100%'} padding={10} className="boxShadow">
                  <Information
                    content={[
                      '오늘 입금될 상품 결제대금과 과거 정산내역, 상품주문별 상세 정산내역을 확인하실 수 있습니다.',
                      '상품주문별 결제대금 및 수수료와 관련된 결제대금 정산, 할인/적립과 관련된 혜택정산, 상품 판매대금에 연계되지 못하고 적용되는 일별/공제 환급, 지급보류 금액을 확인하실 수 있습니다.',
                      '판매 내역은 부가세신고를 돕기 위한 참고 자료를 확인하실 수 있습니다.',
                    ]}
                  />
                </Content>

                <Content maxWidth={'100%'} padding={'0px 10px'}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      borderBottom: '1px solid #EBEBEB',
                      paddingBottom: 10,
                    }}
                  >
                    <h4
                      style={{
                        fontSize: 16,
                        fontWeight: 600,
                        color: '#4A4A4A',
                      }}
                    >
                      이번달 정산 예정일: <span>25</span>일
                    </h4>
                    <div>
                      {/* {[
                        { label: '목록', value: 'list' },
                       { label: '달력', value: 'calendar' },
                      ].map((_item, _index) => (
                        <Button
                          key={`button_${_index}`}
                          size="small"
                          style={{
                            marginLeft: 3,
                            borderColor:
                              _item.value === viewType
                                ? 'var(--primary-color)'
                                : 'rgba(112, 115, 124, 0.2)',
                            color:
                              _item.value === viewType
                                ? 'var(--primary-color)'
                                : '#171719',
                          }}
                          onClick={() => setViewType(_item.value)}
                        >
                          {_item.label}
                        </Button>
                      ))} */}
                    </div>
                  </div>

                  {/* === Statics === */}
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '10px 0px',
                    }}
                  >
                    {[
                      {
                        label: '합계금액',
                        value: allCalculation?.totalPrice,
                      },
                      {
                        label: '정산항목',
                        value: calculateList?.length ?? 0,
                        unit: '건',
                      },
                      {
                        label:
                          authInfo?.type === 'ADMIN' ? '매출이익' : '수수료',
                        value: allCalculation?.totalCommissionPrice,
                      },

                      {
                        label: '정산금액',
                        value: allCalculation?.calculationPrice,
                      },
                    ].map((_item, _index) => (
                      <div
                        style={{
                          borderRight: '1px solid #EBEBEB',
                          width: '33%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <h4
                          style={{
                            fontSize: 18,
                            fontWeight: 700,
                            color: '#4B4B4B',
                            paddingRight: 30,
                          }}
                        >
                          {_item.label}
                        </h4>
                        <p
                          style={{
                            fontSize: 24,
                            fontWeight: 400,
                            color: 'var(--black-color)',
                          }}
                        >
                          {stringToMoneyFormat(
                            _item.value,
                            `${_item.unit ?? '원'}`
                          )}
                        </p>
                      </div>
                    ))}
                  </div>

                  <Content maxWidth={'100%'} padding={'10px 0px'}>
                    {viewType === 'list' ? (
                      <Table
                        size="small"
                        bordered
                        rowSelection={{
                          type: 'checkbox',
                          selectedRowKeys: selectedList?.map(
                            (item) => item.key
                          ),
                          // disabled: true,
                          onChange: (_selectedRowKeys, _selectedRows) => {
                            setSelectedList(_selectedRows);
                          },
                          getCheckboxProps: (record) => ({
                            disabled: record.disabled, // 특정 행의 'disabled' 값에 따라 체크박스 비활성화
                          }),
                        }}
                        dataSource={
                          calculateList.map((item, i) => ({
                            ...item,
                            key:
                              item.type === 'RENTAL'
                                ? `${item.shop_order_id}-${item.rental_id}-${item.rental_payment_id}`
                                : `${item.shop_order_id}-${item.parcel_service_id}-${item.regular_payment_id}`,
                            disabled:
                              item.purchase_status === 'CALCULATE'
                                ? true
                                : false,
                          })) ?? []
                        }
                        // pagination={{ pageSize: 100 }}
                        pagination={false} // 페이지네이션 비활성화
                        scroll={{ y: 680, x: 1200 }} // 테이블 자체 스크롤 설정
                        columns={[
                          {
                            title: '주문번호',
                            key: 'shop_order_id',
                            dataIndex: 'shop_order_id',
                            width: 100,
                            align: 'center',
                            sorter: (a, b) => a.shop_order_id - b.shop_order_id,
                            // render: v =>
                          },
                          {
                            title: '정산',
                            key: 'purchase_status',
                            dataIndex: 'purchase_status',
                            width: 100,
                            align: 'center',
                            render: (val) => (
                              <Tag color={PURCHASE_STATUS[val]?.color}>
                                {PURCHASE_STATUS[val]?.title}
                              </Tag>
                            ),
                            filters: Object.entries(PURCHASE_STATUS).map(
                              ([key, value]) => ({
                                text: value.title,
                                value: key,
                              })
                            ),
                            onFilter: (value, record) =>
                              record.purchase_status === value,
                          },
                          {
                            title: '상품유형',
                            key: 'type',
                            dataIndex: 'type',
                            width: 100,
                            align: 'center',
                            render: (val) => (
                              <Tag color={PRODUCT_TYPE[val].color}>
                                {PRODUCT_TYPE[val].title}
                              </Tag>
                            ),
                            filters: Object.entries(PRODUCT_TYPE).map(
                              ([key, value]) => ({
                                text: value.title,
                                value: key,
                              })
                            ),
                            onFilter: (value, record) => record.type === value,
                          },
                          {
                            title: '상품명',
                            key: 'product',
                            dataIndex: 'product',
                            align: 'center',
                            render: (val) => val.product_name,
                          },
                          {
                            title: '결제일',
                            key: 'created_at',
                            dataIndex: 'created_at',
                            width: 200,
                            align: 'center',
                            render: (val) =>
                              dayjs.unix(val).format('YYYY-MM-DD(HH:mm:ss)'),
                            sorter: (a, b) => a.created_at - b.created_at,
                          },
                          {
                            title: '결제수단',
                            key: 'payment_method',
                            dataIndex: 'payment_method',
                            width: 125,
                            align: 'center',
                            render: (val) => Payment.PAYMENT_METHOD[val],
                            // render: (val) => ,
                            filters: Object.entries(Payment.PAYMENT_METHOD).map(
                              ([key, value]) => ({
                                text: value,
                                value: key,
                              })
                            ),
                            onFilter: (value, record) =>
                              record.payment_method === value,
                          },
                          {
                            title: '결제금액',
                            key: 'payment_price',
                            dataIndex: 'payment_price',
                            width: 105,
                            align: 'center',
                            render: (val) => (
                              <div style={{ textAlign: 'right' }}>
                                {stringToMoneyFormat(val)}
                              </div>
                            ),
                            sorter: (a, b) => a.payment_price - b.payment_price,
                          },
                          {
                            title:
                              authInfo?.type === 'ADMIN'
                                ? '매출이익'
                                : '수수료',
                            key: 'product',
                            dataIndex: 'product',
                            width: 120,
                            align: 'center',
                            render: (val) =>
                              `${stringToMoneyFormat(
                                val?.commission?.commission_rate,
                                val?.commission?.commission_type === 'R'
                                  ? '%'
                                  : '원'
                              )} (${
                                COMMISSION_TYPE[val?.commission?.type]?.label
                              })`,
                          },
                          {
                            title:
                              authInfo?.type === 'ADMIN'
                                ? '정산예정금액(매출이익)'
                                : '정산예정금액(수수료)',
                            key: 'calc_price',
                            dataIndex: 'calc_price',
                            width: 165,
                            align: 'right',
                            fixed: 'right',

                            render: (_, row) => {
                              const price = row?.payment_price ?? 0;
                              const oneProductPrice = row?.product?.price;
                              const rate =
                                row?.product?.commission?.commission_rate ?? 0;
                              const type =
                                row?.product?.commission?.commission_type ??
                                'R';
                              const cms =
                                type === 'R'
                                  ? Math.ceil(price * (rate / 100))
                                  : Math.ceil((price / oneProductPrice) * rate);
                              const total = price - cms;
                              return `${stringToMoneyFormat(
                                total
                              )}(${stringToMoneyFormat(cms)})`;
                            },
                          },
                        ]}
                      />
                    ) : (
                      <Calendar
                        cellRender={() => {}}
                        onSelect={(d) => handleDayClick(d)}
                      />
                    )}
                  </Content>
                </Content>
                {/* === 정산목록 === */}
              </Content>
              {/* ===== 하단 컨텐츠 [업체목록+정산목록] ===== */}
            </Col>
          </Row>
        </Spin>
      </Content>

      {/* ===== 정산모달 - start ===== */}
      <ModalLayout
        type={'modal'}
        open={isModalOpened}
        onCancel={setIsModalOpened}
        centered
        closable
        title={`${dayjs(clickDate ?? new Date()).format('M월')} 일괄정산`}
        width={840}
        footer={
          <>
            <Button
              size="large"
              type="primary"
              style={{ marginRight: 8 }}
              disabled={selectedList && selectedList.length > 0 ? false : true}
              onClick={selectedList && selectedList.length > 0 && onCalculation}
            >
              정산완료
            </Button>
          </>
        }
      >
        <Content
          maxWidth={'100%'}
          padding={0}
          style={{
            borderTop: '1px solid #F4F4F5',
            borderBottom: '1px solid #F4F4F5',
          }}
        >
          <Row>
            <Col x={24}>
              {/* finalCalculation */}
              <Content
                width={'100%'}
                maxWidth={'100%'}
                padding={10}
                style={{ display: 'flex', borderBottom: '1px solid #EBEBEB' }}
              >
                <Row style={{ width: '100%' }}>
                  {[
                    { label: '합계금액', value: finalCalculation?.totalPrice },
                    {
                      label: '정산항목',
                      value: selectedList?.length ?? 0,
                      unit: '건',
                    },
                    {
                      label: '수수료',
                      value: finalCalculation?.totalCommissionPrice,
                    },

                    {
                      label: '정산금액',
                      value: finalCalculation?.calculationPrice,
                    },
                  ].map((_item, _index) => (
                    <Col x={6} key={`calc_confirm_list_${_index}`}>
                      <Statistic
                        title={_item.label}
                        value={stringToMoneyFormat(
                          _item.value,
                          `${_item.unit ?? '원'}`
                        )}
                        style={{ textAlign: 'center' }}
                      />
                    </Col>
                  ))}
                </Row>
              </Content>
            </Col>
            <Col x={24}>
              <Content maxWidth={'100%'} padding={'5px 0px'}>
                <Information
                  content={[
                    '오늘 입금될 상품 결제대금과 과거 정산내역, 상품주문별 상세 정산내역을 확인하실 수 있습니다.',
                    '상품주문별 결제대금 및 수수료와 관련된 결제대금 정산, 할인/적립과 관련된 혜택정산, 상품 판매대금에 연계되지 못하고 적용되는 일별/공제 환급, 지급보류 금액을 확인하실 수 있습니다.',
                    '판매 내역은 부가세신고를 돕기 위한 참고 자료를 확인하실 수 있습니다.',
                  ]}
                />
              </Content>

              <Content maxWidth={'100%'} minHeight={350} padding={5}>
                <Table
                  size="small"
                  bordered
                  rowSelection={{
                    type: 'checkbox',
                    selectedRowKeys: selectedList?.map((item) => item.key),
                    onChange: (_selectedRowKeys, _selectedRows) => {
                      if (_selectedRows.length < selectedList.length) {
                        if (!window.confirm('정산항목을 수정하시겠습니까?'))
                          return;
                      }
                      setSelectedList(_selectedRows);
                    },
                  }}
                  dataSource={selectedList}
                  pagination={false} // 페이지네이션 비활성화
                  scroll={{ y: 680, x: 750 }} // 테이블 자체 스크롤 설정
                  columns={[
                    {
                      title: '주문번호',
                      dataIndex: 'shop_order_id',
                      width: 90,
                      align: 'center',
                    },
                    {
                      title: '상품유형',
                      dataIndex: 'type',
                      width: 100,
                      align: 'center',
                      render: (val) => (
                        <Tag color={PRODUCT_TYPE[val].color}>
                          {PRODUCT_TYPE[val].title}
                        </Tag>
                      ),
                    },
                    {
                      title: '상품명',
                      dataIndex: 'product',
                      align: 'center',
                      width: 245,
                      render: (val) => val.product_name,
                    },
                    {
                      title: '결제일',
                      dataIndex: 'created_at',
                      width: 200,
                      align: 'center',
                      render: (val) =>
                        dayjs.unix(val).format('YYYY-MM-DD(HH:mm:ss)'),
                    },
                    {
                      title: '결제수단',
                      dataIndex: 'payment_method',
                      width: 100,
                      align: 'center',
                      // render: (val) => ,
                    },
                    {
                      title: '결제금액',
                      dataIndex: 'payment_price',
                      width: 100,
                      align: 'center',
                      render: (val) => (
                        <div style={{ textAlign: 'right' }}>
                          {stringToMoneyFormat(val)}
                        </div>
                      ),
                    },
                    {
                      title: authInfo?.type === 'ADMIN' ? '매출이익' : '수수료',
                      key: 'product',
                      dataIndex: 'product',
                      width: 100,
                      align: 'center',
                      render: (val) =>
                        `${stringToMoneyFormat(
                          val?.commission?.commission_rate,
                          val?.commission?.commission_type === 'R' ? '%' : '원'
                        )}`,
                    },
                    {
                      title:
                        authInfo?.type === 'ADMIN'
                          ? '정산예정금액(매출이익)'
                          : '정산예정금액(수수료)',
                      key: 'calc_price',
                      dataIndex: 'calc_price',
                      width: 145,
                      align: 'right',
                      fixed: 'right',
                      render: (_, row) => {
                        const price = row?.payment_price ?? 0;
                        const rate =
                          row?.product?.commission?.commission_rate ?? 0;
                        const type =
                          row?.product?.commission?.commission_type || 'R';
                        const cms = type === 'R' ? price * (rate / 100) : rate;
                        const total = price - cms;

                        return `${stringToMoneyFormat(
                          total
                        )}(${stringToMoneyFormat(cms)})`;
                      },
                    },
                  ]}
                />
              </Content>
            </Col>
          </Row>
        </Content>
      </ModalLayout>
      {/* ===== 정산모달 - end ===== */}
    </>
  ); // JSX: end
};

CalculateListPresenter.defaultProps = {
  shopList: [],
  calculateList: [],
};

const styles = {
  title: {
    fontSize: 24,
    fontWeight: 700,
    marginBottom: 20,
  },
  button: {
    width: 80,
    height: 40,
    fontSize: 15,
    fontWeight: 600,
    margin: '0px 4px',
  },
  form: {
    input: {
      large: {
        width: 175,
        marginRight: 8,
      },
      medium: {
        width: 100,
        marginRight: 8,
      },
    },
    button: {
      marginRight: 8,
    },
    date: {
      width: 120,
    },
    text: {
      fontSize: 13,
      fontWeight: 400,
    },
  },
};

export default CalculateListPresenter;
